import * as THREE from 'three';
import { ThreeEvent } from '@react-three/fiber';
import { BaseTool } from '../BaseTool';
import { WallElement } from '../../core/types';
import { transformSceneObjects } from '../../core/scene/editRouter';
import { scaleMatrix } from '../cad/entityGeometry';

export class ScaleToolClass extends BaseTool {
    id = 'SCALE';
    name = 'Scale';
    cursor = 'crosshair';

    private origin: THREE.Vector3 | null = null;
    private referencePoint: THREE.Vector3 | null = null; // First point of reference length

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const selection = this.context.appStore.selectedIds;
        if (selection.length === 0) {
            // Click-to-select: pick the element under the pointer first; the
            // next clicks place the origin and reference points.
            const id = this.findElementIdAtPoint(event);
            if (id) this.selectElement(id);
            return;
        }

        const point = this.getIntersection(event);
        if (!point) return;
        const snappedPoint = this.snapToGrid(point);

        if (!this.origin) {
            // Step 1: Pick Origin
            this.origin = snappedPoint;
            console.log('Scale: Origin selected', this.origin);
            // Visual feedback
        } else if (!this.referencePoint) {
            // Step 2: Pick Reference Start (defines base length from origin)
            this.referencePoint = snappedPoint;
            console.log('Scale: Reference point selected', this.referencePoint);
        } else {
            // Step 3: Pick Target (defines new length)
            const targetPoint = snappedPoint;
            console.log('Scale: Target point selected', targetPoint);

            this.scaleElements(selection, this.origin, this.referencePoint, targetPoint);

            // Reset
            this.origin = null;
            this.referencePoint = null;
            this.context.appStore.setActiveTool('select');
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        // Scale ghost (Faz 11.8): origin→reference and origin→cursor rays
        // through the shared toolPreview channel (DRAGMODE gates the render
        // side); the live factor rides the dynamic-input readout.
        if (!this.context || !this.origin) return;
        const point = this.getIntersection(event);
        if (!point) return;
        this.context.appStore.setToolPreview({
            active: true,
            toolId: this.id,
            center: this.origin,
            startAngle: this.referencePoint ?? this.origin,
            currentAngle: this.snapToGrid(point),
        });
    }

    onPointerUp(): void { }

    private scaleElements(ids: string[], origin: THREE.Vector3, ref: THREE.Vector3, target: THREE.Vector3) {
        // Calculate scale factor
        const baseLength = origin.distanceTo(ref);
        const newLength = origin.distanceTo(target);

        if (baseLength === 0) return;
        const scaleFactor = newLength / baseLength;

        console.log(`Scale: Scaling by factor ${scaleFactor}`);

        // B4 cross-workbench: foreign citizens scale through the edit router
        // (XY about the origin, level Z preserved — CAD drafting semantics).
        const foreignIds = ids.filter(id => !this.context!.bimStore.project.elements.has(id));
        if (foreignIds.length > 0) {
            transformSceneObjects(
                foreignIds,
                scaleMatrix([origin.x, origin.y], scaleFactor),
                'Scale'
            );
        }

        ids.forEach(id => {
            const element = this.context!.bimStore.project.elements.get(id);
            if (!element) return;

            if (element.category === 'Walls') {
                const wall = element as WallElement;
                const start = new THREE.Vector3(...wall.geometryData.start);
                const end = new THREE.Vector3(...wall.geometryData.end);

                // Scale relative to origin
                // P' = O + (P - O) * s
                const newStart = origin.clone().add(start.clone().sub(origin).multiplyScalar(scaleFactor));
                const newEnd = origin.clone().add(end.clone().sub(origin).multiplyScalar(scaleFactor));

                this.context!.bimStore.updateElement(id, {
                    geometryData: {
                        ...wall.geometryData,
                        start: [newStart.x, newStart.y, newStart.z],
                        end: [newEnd.x, newEnd.y, newEnd.z]
                    }
                });
            }
        });
    }

    deactivate() {
        super.deactivate();
        this.origin = null;
        this.referencePoint = null;
    }
}
